import { memo, type ReactElement } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import VegNonVegIcon from "@/components/common/VegNonVegIcon";
import { fontWeight } from "@/theme";
import {
  getPrimaryFoodTypeAriaLabel,
  getPrimaryFoodTypeLabel,
  type PrimaryFoodType,
} from "@/utils/tag-dietary";

export type PrimaryFoodTypeBadgeProps = {
  type: PrimaryFoodType;
};

type FoodTypeVisual = {
  label: string;
  ariaLabel: string;
  icon?: "veg" | "non-veg" | "vegan";
};

function resolveFoodTypeVisual(type: PrimaryFoodType): FoodTypeVisual {
  return {
    label: getPrimaryFoodTypeLabel(type),
    ariaLabel: getPrimaryFoodTypeAriaLabel(type),
    icon:
      type === "NON_VEG"
        ? "non-veg"
        : type === "VEGAN"
          ? "vegan"
          : type === "VEG" || type === "VEGETARIAN" || type === "EGGITARIAN"
            ? "veg"
            : undefined,
  };
}

/**
 * Primary food identity — colored icon with neutral short label (no chip chrome).
 */
export const PrimaryFoodTypeBadge = memo(function PrimaryFoodTypeBadge({
  type,
}: PrimaryFoodTypeBadgeProps): ReactElement {
  const theme = useTheme();
  const visual = resolveFoodTypeVisual(type);

  return (
    <Box
      component="span"
      role="status"
      aria-label={`Food type: ${visual.ariaLabel}`}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        alignSelf: "flex-start",
        maxWidth: "100%",
        minHeight: 20,
      }}
    >
      {visual.icon === "veg" || visual.icon === "non-veg" ? (
        <VegNonVegIcon type={visual.icon === "veg" ? "VEG" : "NON_VEG"} size={16} />
      ) : visual.icon === "vegan" ? (
        <Typography
          component="span"
          aria-hidden="true"
          sx={{ fontSize: theme.typography.pxToRem(14), lineHeight: 1 }}
        >
          🌱
        </Typography>
      ) : null}
      <Typography
        component="span"
        sx={{
          fontSize: theme.typography.pxToRem(13),
          fontWeight: fontWeight.medium,
          lineHeight: 1.2,
          color: theme.palette.text.secondary,
        }}
      >
        {visual.label}
      </Typography>
    </Box>
  );
});
